import type { AuditLogDetail } from '@circuitry-ai/doc-data';

export function getAuditTypeChipClass(auditType: string): string {
  switch (auditType?.toLowerCase()) {
    case 'trigger':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'update':
      return 'bg-primary/10 text-primary border-primary';
    case 'create':
      return 'bg-success/10 text-success border-success';
    case 'delete':
      return 'bg-danger/10 text-danger border-danger';
    default:
      return 'bg-default-100 text-default-700 border-default-200';
  }
}

export function getAuditStatusChipClass(status: string): string {
  switch (status?.toLowerCase()) {
    case 'success':
      return 'bg-success/10 text-success border-success';
    case 'failed':
      return 'bg-danger/10 text-danger border-danger';
    default:
      return 'bg-default-100 text-default-700 border-default-200';
  }
}

export type AuditLogSummaryMetricKey =
  | 'row_count'
  | 'distinct_claim_count'
  | 'successful_count'
  | 'failed_count'
  | 'skipped_count';

const SUMMARY_METRIC_KEYS: AuditLogSummaryMetricKey[] = [
  'row_count',
  'distinct_claim_count',
  'successful_count',
  'failed_count',
  'skipped_count',
];

function parseMetricNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
}

function collectMetricSources(detail?: AuditLogDetail | null): Record<string, unknown>[] {
  if (!detail) return [];

  const detailRecord = detail as unknown as Record<string, unknown>;
  const metadata = (detail.metadata ?? {}) as Record<string, unknown>;
  const metadataNestedSummary = (metadata.reprocess_summary ?? metadata.summary) as
    | Record<string, unknown>
    | undefined;
  const detailNestedSummary = (detail.reprocess_summary ??
    detailRecord.reprocess_summary ??
    detailRecord.summary) as Record<string, unknown> | undefined;

  const sources: Record<string, unknown>[] = [];

  if (detailNestedSummary && Object.keys(detailNestedSummary).length > 0) {
    sources.push(detailNestedSummary);
  }
  sources.push(detailRecord);
  if (metadataNestedSummary && Object.keys(metadataNestedSummary).length > 0) {
    sources.push(metadataNestedSummary);
  }
  sources.push(metadata);

  return sources;
}

/** Resolves reprocess summary counts from root, metadata, or nested summary objects. */
export function getAuditLogSummaryMetric(
  detail: AuditLogDetail | undefined,
  key: AuditLogSummaryMetricKey
): number | undefined {
  for (const source of collectMetricSources(detail)) {
    const value = parseMetricNumber(source[key]);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function getAuditLogSummaryMetrics(
  detail?: AuditLogDetail | null
): Partial<Record<AuditLogSummaryMetricKey, number>> {
  return SUMMARY_METRIC_KEYS.reduce(
    (acc, key) => {
      const value = getAuditLogSummaryMetric(detail ?? undefined, key);
      if (value !== undefined) acc[key] = value;
      return acc;
    },
    {} as Partial<Record<AuditLogSummaryMetricKey, number>>
  );
}
